import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  saveErrorReason,
  useAdminFormPersistence,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchAction, BatchFailure } from "@components/CatalogIndex";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetAccountQuery, useV2GetProjectByIdQuery } from "@generated";
import { projectChromeQueryArgs } from "@components/SowHeader/projectChromeQuery";
import useChangeOrderAccess from "../hooks/useChangeOrderAccess";
import { useChangeOrderMutations, useChangeOrdersList } from "../api";
import { isLocked, type ChangeOrder } from "../types";
import { buildColumns, type FieldLabels } from "./columns";
import SummaryInfo from "./SummaryInfo";
import { changeOrderChild } from "../utils/changeOrderChild";

interface Props {
  projectId: string;
}

export const LOCKED_DELETE_TOOLTIP =
  "Approved or won change orders can't be deleted";
export const NOT_OWN_DELETE_TOOLTIP =
  "You can only delete your own change orders";
export const NOT_OWN_OR_LOCKED_DELETE_TOOLTIP =
  "You can only delete your own change orders that aren't approved or won";

type Access = ReturnType<typeof useChangeOrderAccess>;

/** Why the selection can't be deleted, or undefined when it can. */
export const deleteBlockedReason = (
  selectedRows: ChangeOrder[],
  access: Pick<Access, "canEdit">
): string | undefined => {
  const notOwn = selectedRows.some((r) => !access.canEdit(r));
  const locked = selectedRows.some(
    (r) => access.canEdit(r) && isLocked(r.status)
  );
  if (notOwn && locked) return NOT_OWN_OR_LOCKED_DELETE_TOOLTIP;
  if (notOwn) return NOT_OWN_DELETE_TOOLTIP;
  if (locked) return LOCKED_DELETE_TOOLTIP;
  return undefined;
};

const List = ({ projectId }: Props) => {
  const navigate = useNavigate();
  const access = useChangeOrderAccess();
  const { rows, totalPages, page, setPage, isLoading, isFetching, refetch } =
    useChangeOrdersList(projectId);
  const { remove } = useChangeOrderMutations();

  // Shares the project chrome's cache entry (options don't change the key).
  // Refetched on mount and after deletes: the generated API has no cache tags,
  // so nothing else refreshes change-order-summary.
  const { data: projectData, refetch: refetchProject } =
    useV2GetProjectByIdQuery(projectChromeQueryArgs(projectId), {
      refetchOnMountOrArgChange: true,
    });
  const { data: accountData } = useV2GetAccountQuery({});

  const project = projectData?.data;
  const summary = project?.attributes?.["change-order-summary"];
  const isChild = changeOrderChild(project).isChangeOrder;
  const projectFieldLabels = project?.attributes?.["field-labels"] as
    | FieldLabels
    | undefined;
  const currency = projectFieldLabels?.currency_unit ?? "$";
  const accountFieldLabels = accountData?.data?.attributes?.["field-labels"] as
    | FieldLabels
    | undefined;

  const columns = useMemo(
    () => buildColumns({ fieldLabels: accountFieldLabels, currency }),
    [accountFieldLabels, currency]
  );

  const sel = useBatchSelection([page]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Picks up a toast from New/Edit (`navigate("..", { state: { toast } })`).
  const { toast, setToast } = useAdminFormPersistence();

  if (isLoading) return <ScopeStackSpinner />;

  const selectedRows = rows.filter((r) => sel.selectedIds.includes(r.id));
  const allDeletable =
    selectedRows.length > 0 && selectedRows.every(access.canDelete);

  const handleConfirmDelete = async () => {
    const failures: BatchFailure[] = [];
    for (const row of selectedRows) {
      try {
        await remove(row.id);
      } catch (err) {
        failures.push({ label: row.label, reason: saveErrorReason(err) });
      }
    }
    const total = selectedRows.length;
    setToast(
      failures.length === 0
        ? { variant: "success", text: `${total} change order(s) deleted.` }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed to delete ${failures.length} of ${total} change orders.`,
              failures
            ),
          }
    );
    sel.clear();
    setConfirmOpen(false);
    refetch();
    refetchProject();
  };

  const batchActions: BatchAction[] = access.canDeleteAny
    ? [
        {
          icon: faTrashCan,
          label: "Delete",
          variant: "danger",
          onClick: () => setConfirmOpen(true),
          disabled: !allDeletable,
          tooltip: allDeletable
            ? undefined
            : deleteBlockedReason(selectedRows, access),
        },
      ]
    : [];

  const isEmpty = rows.length === 0 && !isFetching;
  // A change-order project can't have change orders of its own.
  const canCreateHere = access.canCreate && !isChild;

  return (
    <>
      <AdminListCard
        title="Change Orders"
        onNew={canCreateHere ? () => navigate("new") : undefined}
        newButtonLabel="+ New Change Order"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
        extraInfo={
          summary ? (
            <SummaryInfo summary={summary} currency={currency} />
          ) : undefined
        }
      >
        {isEmpty ? (
          <div className="mt-3 px-3">
            <p className="text-muted mb-1">No change orders yet.</p>
            {canCreateHere && (
              <p className="text-muted" data-testid="change-orders-explainer">
                A change order adds scope to a signed project. It copies the
                lines you pick into a new project that is approved on its own,
                and once approved or won its revenue and hours count toward this
                project's totals.
              </p>
            )}
          </div>
        ) : (
          <AdminListTable
            columns={columns}
            data={rows}
            isLoading={isFetching}
            selectable={access.canDeleteAny}
            selectedIds={sel.selectedIds}
            setSelectedIds={sel.setSelectedIds}
            onEdit={(id) => navigate(`${id}/edit`)}
            editColumnId="label"
            paginationEnabled={totalPages > 1}
            totalPages={totalPages}
            currentPage={page}
            setCurrentPage={setPage}
          />
        )}
      </AdminListCard>
      <ConfirmDeleteModal
        open={confirmOpen}
        title={`Delete change orders? (${selectedRows.length})`}
        message={`This permanently deletes ${selectedRows.length} change order(s) and their change-order projects. Continue?`}
        confirmLabel="Permanently Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
